import React, { ChangeEvent, Component } from 'react';
import onClickOutside from 'react-onclickoutside';
import { LoadingIndicator } from '@mlc/web-ui-toolkit';
import palette from '@mlc/mlc-palette';
import * as Styled from './styled';
import topMenuTestIds from '../TopMenu.elements';
import Portal from '../../Common/Portal/Portal';
import {
	setMenuToggleHandler,
	toggleMenuOnTab,
	toggleMenuOnShiftTab,
} from '../../../../logic/functions/common/menuDisplayToggle';
import { searchModalElements } from './SearchModalElements';
import { globalSearch } from '../../../../logic/functions/globalSearch.function';

interface GlobalSearchResultItem {
	id: number;
	label: string;
}
interface SearchModalProps {
	parentRef: React.RefObject<HTMLDivElement>;
	onClickOutside(event: any): void;
	onKeywordUpdate(keyword: string): void;
	searchText?: string;
}

interface SearchModalState {
	suggestions: Array<GlobalSearchResultItem>;
	searchText?: string;
	loading: boolean;
}

export class SearchModal extends Component<SearchModalProps, SearchModalState> {
	public inputRef: React.RefObject<HTMLInputElement>;

	constructor(props: SearchModalProps) {
		super(props);
		this.inputRef = React.createRef();
		this.handleSearchInputChange = this.handleSearchInputChange.bind(this);
		setMenuToggleHandler(this.props.onClickOutside);
		this.state = {
			suggestions: [],
			loading: false,
		};
	}

	handleClickOutside = (event: any) => {
		this.props.onClickOutside(event);
	};

	componentDidMount() {
		//@ts-ignore
		this.inputRef.focus();
	}

	shouldRenderSuggestions = (value: string) => value.trim().length > 2;

	renderSuggestion = (suggestion: GlobalSearchResultItem) => {
		return (
			<Styled.AutosuggestResultItem data-analytics-id="search-autosuggest-result">
				{suggestion.label}
			</Styled.AutosuggestResultItem>
		);
	};

	getSuggestionValue = (suggestion: GlobalSearchResultItem) => suggestion.label;

	handleSuggestionSelected = (event: any, { suggestionValue }: any) => {
		event.preventDefault();
		this.redirectToSearchResults(suggestionValue);
	};

	handleSuggestionsFetchRequested = async ({ value }: any) => {
		if (value.length <= 2) {
			this.setState({
				suggestions: [],
			});
			return;
		}
		this.setState({
			loading: true,
		});

		const searchData = await globalSearch(value);
		this.setState({ suggestions: searchData, loading: false });
	};

	handleSuggestionsClearRequested = () => {
		this.setState({ suggestions: [] });
	};

	handleFormSubmit = (event: any) => {
		event.preventDefault();
		this.redirectToSearchResults(this.props.searchText);
	};

	handleSearchInputChange(e: ChangeEvent<HTMLInputElement>) {
		this.props.onKeywordUpdate(e.target.value);
	}

	redirectToSearchResults = (searchText: string | undefined) => {
		if (searchText) {
			const base = '/secure/adviser/site_tools/search-results#/?q=';
			const url = `${base}${searchText}&searchFilterType=webpage&p=1`;
			window.location.href = url;
		}
	};
	renderInputComponent = ({ ref, ...rest }: any) => {
		const { loading } = this.state;
		return (
			<Styled.InputContainer>
				<input
					type="text"
					{...rest}
					ref={(inputRef: React.RefObject<HTMLInputElement>) => {
						this.inputRef = inputRef;
					}}
				/>
				{loading ? (
					<Styled.LoadingWrapper>
						<LoadingIndicator color={palette.mlcOrange} />
					</Styled.LoadingWrapper>
				) : null}
			</Styled.InputContainer>
		);
	};

	render() {
		const { suggestions } = this.state;
		const inputProps = {
			onChange: this.handleSearchInputChange,
			placeholder: searchModalElements.placeholder,
			value: this.props.searchText || '',
		};

		return (
			<Portal node={document.getElementById(topMenuTestIds.topMenuSearch)}>
				<Styled.ResponsiveContainer
					parentRef={this.props.parentRef.current}
					position={{ left: -50, right: 0, width: 390 }}
					alignment="right"
					dataId={topMenuTestIds.topMenuSearchModalContainer}
				>
					<Styled.SearchForm
						onSubmit={this.handleFormSubmit}
						onKeyUp={(event: any) => toggleMenuOnTab(event, false)}
						onKeyDown={(event: any) => toggleMenuOnShiftTab(event, false)}
					>
						<Styled.Autosuggest
							aria-labelledby={searchModalElements.ariaLabelledBy}
							id={searchModalElements.autoSuggestId}
							data-id={searchModalElements.autoSuggestId}
							data-analytics-id={searchModalElements.analyticsId}
							multiSection={false}
							suggestions={suggestions}
							onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
							onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
							onSuggestionSelected={this.handleSuggestionSelected}
							getSuggestionValue={this.getSuggestionValue}
							inputProps={inputProps}
							getSectionSuggestions={(section: any) => section}
							shouldRenderSuggestions={this.shouldRenderSuggestions}
							renderSuggestion={this.renderSuggestion}
							renderInputComponent={this.renderInputComponent}
						/>
						<Styled.SearchButtonContainer>
							<Styled.SearchButton data-id={topMenuTestIds.topMenuSearchButton}>
								Search
							</Styled.SearchButton>
						</Styled.SearchButtonContainer>
					</Styled.SearchForm>
				</Styled.ResponsiveContainer>
			</Portal>
		);
	}
}

export default onClickOutside(SearchModal);
